/**
 * Lightweight, dependency-free device fingerprint used to spot repeat free-trial signups.
 *
 * It only combines signals that stay stable across browser updates (no user agent
 * version), and the result is a hash, never the raw signals. The server hashes it again
 * before storing it. It is a soft signal: a miss or collision only changes which CTAs
 * are shown, never whether someone can sign up.
 */

/* eslint-disable no-bitwise */
// cyrb53: fast 53-bit string hash. Two seeds give ~106 bits, plenty for this purpose.
var cyrb53 = function cyrb53(str) {
  var seed = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var h1 = 0xdeadbeef ^ seed;
  var h2 = 0x41c6ce57 ^ seed;
  for (var i = 0; i < str.length; i += 1) {
    var ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
};
/* eslint-enable no-bitwise */

var safely = function safely(fn) {
  try {
    var _fn;
    return (_fn = fn()) !== null && _fn !== void 0 ? _fn : '';
  } catch (_unused) {
    return '';
  }
};
var canvasSignal = function canvasSignal() {
  return safely(function () {
    var canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    var ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '16px "Arial"';
    ctx.fillStyle = '#f60';
    ctx.fillRect(100, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText("Rubriq fp \uD83D\uDD0D 1.0", 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText("Rubriq fp \uD83D\uDD0D 1.0", 4, 17);
    return cyrb53(canvas.toDataURL());
  });
};
var webglSignal = function webglSignal() {
  return safely(function () {
    var gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return '';
    var info = gl.getExtension('WEBGL_debug_renderer_info');
    return info ? "".concat(gl.getParameter(info.UNMASKED_VENDOR_WEBGL), "~").concat(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : "".concat(gl.getParameter(gl.VENDOR), "~").concat(gl.getParameter(gl.RENDERER));
  });
};
var collectSignals = function collectSignals() {
  var nav = window.navigator || {};
  var scr = window.screen || {};
  // Sorted so rotating a device doesn't change the value.
  var screenSize = [scr.width, scr.height].sort(function (a, b) {
    return a - b;
  }).join('x');
  return [nav.platform, nav.language, (nav.languages || []).join(','), nav.hardwareConcurrency, nav.deviceMemory, nav.maxTouchPoints, screenSize, scr.colorDepth, window.devicePixelRatio, safely(function () {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }), canvasSignal(), webglSignal()].map(function (value) {
    return value === undefined || value === null ? '' : String(value);
  }).join('|');
};
var cached = null;

/** Returns the device fingerprint, or '' if it can't be computed. Never throws. */
export var getDeviceFingerprint = function getDeviceFingerprint() {
  if (cached === null) {
    var signals = safely(collectSignals);
    cached = signals ? "v1.".concat(cyrb53(signals, 1)).concat(cyrb53(signals, 2)) : '';
  }
  return cached;
};
export default getDeviceFingerprint;